<template>
  <div class="page-container">
    <div class="page-header">
      <h2 class="page-title">字数报表</h2>
      <p class="page-subtitle">余额 / 消费 / 充值一目了然</p>
    </div>

    <el-card class="summary-card" shadow="never">
      <div class="summary-grid">
        <div class="sum-item">
          <div class="sum-label">当前余额</div>
          <div class="sum-value">{{ formatBalance(summary.balance) }}</div>
        </div>
        <div class="sum-item">
          <div class="sum-label">期间消费字数</div>
          <div class="sum-value danger">{{ summary.consume_total.toLocaleString() }}</div>
        </div>
        <div class="sum-item">
          <div class="sum-label">期间充值字数</div>
          <div class="sum-value success">{{ formatRechargeTotal(summary.recharge_total) }}</div>
        </div>
        <div class="sum-item action">
          <el-button type="primary" @click="openRecharge">手动充值</el-button>
        </div>
      </div>

      <div class="filter-row">
        <el-date-picker v-model="dateRange" type="daterange" range-separator="至" start-placeholder="开始日期"
          end-placeholder="结束日期" value-format="YYYY-MM-DD" />
        <el-button type="primary" @click="handleQuery">查询</el-button>
        <el-button @click="resetDate">重置</el-button>
      </div>
    </el-card>

    <el-card class="list-card" shadow="never">
      <el-tabs v-model="activeTab" @tab-change="fetchList">
        <!-- 消费记录 -->
        <el-tab-pane label="消费记录" name="consume">
          <div class="toolbar">
            <div></div>
            <div>
              <el-button @click="exportCsv('consume')">导出CSV</el-button>
            </div>
          </div>
          <el-table :data="tableData" border stripe v-loading="loading">
            <el-table-column prop="id" label="ID" width="80" />
            <el-table-column prop="create_time" label="时间" width="170" />
            <el-table-column prop="action" label="动作" width="100" />
            <el-table-column prop="novel_id" label="小说ID" width="100" />
            <el-table-column prop="chapter_id" label="章节ID" width="100" />
            <el-table-column prop="chapters" label="章数" width="80" />
            <el-table-column prop="words" label="消费字数" width="120" />
            <el-table-column prop="balance_before" label="余额前" width="120" />
            <el-table-column prop="balance_after" label="余额后" width="120" />
            <el-table-column prop="remark" label="备注" />
            <el-table-column label="操作" fixed="right" width="110">
              <template #default="{ row }">
                <el-button size="small" type="primary" @click="openEditor(row.novel_id, row.chapter_id)"
                  :disabled="!row.novel_id">
                  查看详情
                </el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination v-model:current-page="pager.page" v-model:page-size="pager.limit" :total="Number(pager.total)"
            :page-sizes="[10, 20, 50, 100]" layout="total, sizes, prev, pager, next, jumper" @size-change="fetchList"
            @current-change="fetchList" style="margin-top: 12px; justify-content: flex-end;" />
        </el-tab-pane>

        <!-- 充值记录 -->
        <el-tab-pane label="充值记录" name="recharge">
          <div class="toolbar">
            <div></div>
            <div>
              <el-button @click="exportCsv('recharge')">导出CSV</el-button>
            </div>
          </div>
          <el-table :data="tableData" border stripe v-loading="loading">
            <el-table-column prop="id" label="ID" width="80" />
            <el-table-column prop="create_time" label="时间" width="170" />
            <el-table-column prop="amount_words" label="字数" width="120">
              <template #default="{ row }">
                {{ formatAmountWords(row.amount_words) }}
              </template>
            </el-table-column>
            <el-table-column prop="remark" label="备注" />
          </el-table>
          <el-pagination v-model:current-page="pager.page" v-model:page-size="pager.limit" :total="Number(pager.total)"
            :page-sizes="[10, 20, 50, 100]" layout="total, sizes, prev, pager, next, jumper" @size-change="fetchList"
            @current-change="fetchList" style="margin-top: 12px; justify-content: flex-end;" />
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <!-- 手动充值弹窗 -->
    <!-- 卡密充值弹窗（仅输入卡密） -->
    <el-dialog v-model="rechargeVisible" title="卡密充值" width="420px">
      <el-form :model="rechargeForm" label-width="100px">
        <el-form-item label="激活码" required>
          <el-input v-model="rechargeForm.activation_code" placeholder="请输入卡密（激活码）" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="rechargeVisible = false">取消</el-button>
        <el-button type="primary" :loading="recharging" @click="doRecharge">确认充值</el-button>
      </template>
    </el-dialog>

  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import { ElMessage } from 'element-plus'
import config from '@/config'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()

const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
const storeId = userInfo.id

const summary = reactive({
  balance: 0,
  consume_total: 0,
  recharge_total: 0,
  daily: []
})
const dateRange = ref([])
const activeTab = ref('consume')
const loading = ref(false)
const tableData = ref([])
const pager = reactive({ page: 1, limit: 10, total: 0 })
const rechargeForm = reactive({
  activation_code: ''
})

const rechargeVisible = ref(false)
const recharging = ref(false)

// 格式化余额显示
function formatBalance(balance) {
  if (balance === -1 || balance === '-1') {
    return '无限制'
  }
  return Number(balance).toLocaleString()
}
function getDateParams() {
  const params = { store_id: storeId }
  if (dateRange.value && dateRange.value.length === 2) {
    params.start_date = dateRange.value[0]
    params.end_date = dateRange.value[1]
  }
  return params
}

// 格式化充值总额显示
function formatRechargeTotal(value) {
  if (value === -1 || value === '-1') {
    return '无限制'
  }
  return Number(value).toLocaleString()
}

// 格式化充值记录中的字数显示
function formatAmountWords(value) {
  if (value === -1 || value === '-1') {
    return '无限制'
  }
  return Number(value).toLocaleString()
}


async function fetchSummary() {
  try {
    const params = { action: 'summary', ...getDateParams() }
    const { data } = await axios.get(`${config.BASE_URL}/shop/api/word_report_api.php`, { params })
    if (data.code === 200) {
      Object.assign(summary, data.data || {})
    }
  } catch (e) {
    ElMessage.error('汇总获取失败')
  }
}
async function fetchList() {
  try {
    loading.value = true
    const base = getDateParams()
    const params = {
      ...base,
      action: activeTab.value === 'consume' ? 'consume_list' : 'recharge_list',
      page: pager.page,
      limit: pager.limit
    }
    const { data } = await axios.get(`${config.BASE_URL}/shop/api/word_report_api.php`, { params })
    if (data.code === 200) {
      tableData.value = data.data.list || []
      pager.total = Number(data.data.pagination.total || 0)
    }
  } catch (e) {
    ElMessage.error('列表获取失败')
  } finally {
    loading.value = false
  }
}

function handleQuery() {
  pager.page = 1
  fetchSummary()
  fetchList()
}

function resetDate() {
  dateRange.value = []
  pager.page = 1
  fetchSummary()
  fetchList()
}

function exportCsv(type) {
  const base = getDateParams()
  const q = new URLSearchParams({ ...base, action: 'export_csv', type })
  const url = `${config.BASE_URL}/shop/api/word_report_api.php?${q.toString()}`
  window.open(url, '_blank')
}

function openRecharge() {
  rechargeForm.activation_code = ''
  rechargeVisible.value = true
}

async function doRecharge() {
  try {
    if (!rechargeForm.activation_code) {
      ElMessage.warning('请输入激活码'); return
    }
    recharging.value = true
    const payload = {
      store_id: storeId,                          // 直接传 store_id
      activation_code: rechargeForm.activation_code
    }
    const { data } = await axios.post(`${config.BASE_URL}/shop/api/word_recharge_card_api.php`, payload)
    if (data.code === 200) {
      ElMessage.success('充值成功')
      rechargeVisible.value = false
      fetchSummary()
      if (activeTab.value === 'recharge') { fetchList() }
    } else {
      ElMessage.error(data.message || '充值失败')
    }
  } catch (e) {
    ElMessage.error('充值失败')
  } finally {
    recharging.value = false
  }
}


// 跳转创作页（假设路径 /novel/editor?novel_id=&chapter_id=）
function openEditor(novel_id, chapter_id) {
  if (!novel_id) return
  const query = { novel_id: String(novel_id) }
  if (chapter_id) query.chapter_id = String(chapter_id)
  router.push({ path: '/novel/novellist', query })
}

onMounted(() => {
  // 接收仪表盘传入的日期和默认标签
  const qsStart = route.query.start_date
  const qsEnd = route.query.end_date
  const qsTab = route.query.tab
  if (qsStart && qsEnd) {
    dateRange.value = [String(qsStart), String(qsEnd)]
  }
  if (qsTab) activeTab.value = String(qsTab)

  fetchSummary()
  fetchList()
})
</script>

<style scoped lang="scss">
.page-container {
  background: linear-gradient(135deg, #f5f7fa, #e4e8ed);
  min-height: 100vh;
}

.page-header {
  padding: 24px 30px;
  background: #fff;
  margin-bottom: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, .05);
}

.page-title {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
}

.page-subtitle {
  margin-top: 6px;
  color: #666;
}

.summary-card {
  margin-left: 15px;
  margin-right: 15px;
}

.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 12px;
  align-items: center;
}

.sum-item {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 12px;
}

.sum-item.action {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background: transparent;
  border: none;
}

.sum-label {
  color: #888;
  font-size: 12px;
  margin-bottom: 6px;
}

.sum-value {
  font-size: 22px;
  font-weight: 700;
}

.sum-value.danger {
  color: #f56c6c;
}

.sum-value.success {
  color: #67c23a;
}

.filter-row {
  margin-top: 12px;
  display: flex;
  gap: 8px;
  align-items: center;
}

.list-card {
  margin-left: 15px;
  margin-right: 15px;
  margin-top: 15px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

@media (max-width: 992px) {
  .summary-grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>